import { configColor, getPathByLink } from "@/utils";
import { FlightDetailsAtom, HideCrumbsAtom } from "../../atoms";
import { useRecoilValue } from "recoil";
import { useIsMobile } from "../../hooks";
import { IVisoreLancioApiResponse } from "../../interfaces/IVisoreLancioDTO";

import dynamic from "next/dynamic";
const Chip = dynamic(() => import("../ChipList/Chip"), { ssr: false });

import DOMPurify from "isomorphic-dompurify";
import SearchPanel from "../SearchPanel/SearchPanel";
import ModuleComponentProps from "../../interfaces/ModuleComponent";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import ModuleThreeWrapper from "../ModuleThreeWrapper/ModuleThreeWrapper";
import TrackingButton from "../Tracking/TrackingButton";

import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

const VisoreLancio: React.FC<ModuleComponentProps> = ({ data, language }: ModuleComponentProps) => {
  const moduleData = data as IVisoreLancioApiResponse;

  const isHideCrumbs = useRecoilValue(HideCrumbsAtom);
  const { i18n, t } = useTranslation();
  const lang = language ? language : i18n.language;

  const [isMobile] = useIsMobile();
  const flightDetails = useRecoilValue(FlightDetailsAtom);
  const [destinationData, setDestinationData] = useState<any>();
  const [imagePath, setImagePath] = useState("");

  const bgCtaColor =
    moduleData?.data?.enuBackgroundCta ? configColor("bg", moduleData?.data?.enuBackgroundCta) : "bg-black";
  const linkPath = moduleData?.data?.lblLink ? moduleData?.data?.lblLink[lang] : "";
  const lblCtaColor =
    moduleData?.data?.enuBackgroundLabel ? configColor("text", moduleData?.data?.enuBackgroundLabel) : "text-white";
  const textColor =
    moduleData?.data?.enuTitleColor ? configColor("text", moduleData?.data?.enuTitleColor) : "text-white";

  const getImageClasses = () => {
    switch (moduleData?.data?.enuImageHeight) {
      case "bassa":
        return "h-72 desk:h-80";
      case "media":
        return "h-[22rem] desk:h-[24rem]";
      case "alta":
        return "h-[28rem] desk:h-[32rem]";
      default:
        return "h-[22rem] desk:h-[24rem]";
    }
  };

  async function getDestinatioData() {
    if (flightDetails && flightDetails.flightId) {
      const leg = flightDetails?.routing[0]?.airportIata === "MXP" || flightDetails?.routing[0]?.airportIata === "LIN" ? 1 : 0;
      const destination = moduleData.data.xtaxDestinations.ti?.find((d: any) => {
        return flightDetails.routing[leg].airportDescription === d.lblAirportDestinationDescription;
      });

      if (destination?.targeturl) {
        try {
          const response = await fetch(destination.targeturl);
          const jsonData = await response.json();
          setDestinationData(jsonData);
        } catch {
          setDestinationData(undefined);
        }
      }
    }
    return null;
  }

  function getImagePath() {
    let mediaserverpath = "";
    let resourcename = "";

    if (!isMobile) {
      if (!destinationData?.data?.upiImageDesktop?.mediaserverpath) {
        mediaserverpath = moduleData?.data?.upiImageDesk?.mediaserverpath ?? "";
        resourcename = moduleData?.data?.upiImageDesk?.resourcename ?? "";
      } else {
        mediaserverpath = destinationData?.data?.upiImageDesktop?.mediaserverpath ?? "";
        resourcename = destinationData?.data?.upiImageDesktop?.resourcename ?? "";
      }
    } else {
      if (!destinationData?.data?.upiImageMobile?.mediaserverpath) {
        mediaserverpath = moduleData?.data?.upiImageMobile?.mediaserverpath ?? "";
        resourcename = moduleData?.data?.upiImageMobile?.resourcename ?? "";
      } else {
        mediaserverpath = destinationData?.data?.upiImageMobile?.mediaserverpath ?? "";
        resourcename = destinationData?.data?.upiImageMobile?.resourcename ?? "";
      }
    }

    setImagePath(mediaserverpath && resourcename ? `${mediaserverpath}/${resourcename}` : "");
  }

  const switchType = () => {
    switch (moduleData?.data?.enuTypeInfo) {
      case "visore con M02-Widget-lancio in overlay":
        return <SearchPanel language={lang} targetUrl={moduleData?.data.xconM02.targeturl} data={null} />;
      case "visore con M03-Widget custom":
        return <ModuleThreeWrapper language={lang} targetUrl={moduleData?.data.xconM03.targeturl} data={null} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    getDestinatioData();
    getImagePath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightDetails, isMobile, destinationData]);

  const renderTitleBlock = () => {
    if (moduleData?.data?.lblTitle && moduleData?.data?.lblTitle[lang]) {
      return (
        <div className="flex flex-col w-full">
          <h1
            className="font-semibold text-[33px] desk:text-5xl leading-[34px] font-secondary"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(moduleData?.data?.lblTitle[lang]) }}
          />
          <h2
            className="text-xl font-primary font-normal desk:text-2xl mt-4"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                moduleData?.data?.lblSubtitle && moduleData?.data?.lblSubtitle[lang]
                  ? moduleData?.data?.lblSubtitle[lang]
                  : ""
              ),
            }}
          />
        </div>
      );
    }

    if (flightDetails && flightDetails.flightId && flightDetails.routing?.length) {
      const isArrivingToMXPorLIN =
        flightDetails?.routing[0]?.airportIata === "MXP" || flightDetails?.routing[0]?.airportIata === "LIN";

      const text =
        flightDetails.routing.length <= 2
          ? isArrivingToMXPorLIN
            ? `${flightDetails?.airlineDescription} ${flightDetails?.flightNumber}${t("flights.flightTo")}${flightDetails?.routing[1]?.airportLabel}`
            : `${flightDetails?.airlineDescription} ${flightDetails?.flightNumber}${t("flights.flightFrom")}${flightDetails?.routing[0]?.airportLabel}`
          : flightDetails.movementType === "D"
          ? `${flightDetails?.airlineDescription} ${flightDetails?.flightNumber}${t("flights.flightTo")}${flightDetails?.routing[2]?.airportLabel}`
          : `${flightDetails?.airlineDescription} ${flightDetails?.flightNumber}${t("flights.flightFrom")}${flightDetails?.routing[0]?.airportLabel}`;

      return (
        <div className="flex w-full items-center justify-between">
          <h3 className="font-semibold text-2xl desk:text-3xl desk:leading-[34px] leading-7 font-secondary">
            {text}
          </h3>
          <TrackingButton flightDetails={flightDetails} />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="desk:pb-14">
      <div
        className={`bg-cover relative bg-no-repeat bg-center ${getImageClasses()}`}
        style={{ backgroundImage: imagePath ? `url(${imagePath})` : undefined }}
      >
        <div className="max-w-cont-1 mx-auto w-full px-6 h-full flex flex-col">
          {!isHideCrumbs ? (
            <div className="pt-4">
              <Breadcrumbs language={lang} colorCrumbs={moduleData?.data?.enuTitleColor} />
            </div>
          ) : null}

          <div className="flex-1">
            <div className={`${textColor} mt-[6vh] desk:mt-[8vh]`}>
              <div className="font-bold text-4xl desk:text-5xl font-secondary">
                {renderTitleBlock()}

                {moduleData?.data?.lblLabelCta && moduleData?.data?.lblLabelCta[lang] ? (
                  <div className="flex items-center text-xl mt-6">
                    <Chip
                      text={moduleData?.data?.lblLabelCta[lang]}
                      bgColor={bgCtaColor}
                      txtColor={lblCtaColor}
                      hrefPath={linkPath !== "" ? linkPath : getPathByLink(moduleData?.data, lang)}
                      width="max-w-full !whitespace-normal"
                      onClick={() => {}}
                      target={moduleData?.data?.flgTarget ? "_blank" : "_self"}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full relative max-w-cont-2 mx-auto desk:-mt-[60px] -mt-[20px] px-6 z-20">
        {switchType()}
      </div>
    </div>
  );
};

export default VisoreLancio;
